import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MapPin, DoorOpen } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export default function LocationsList() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Standorte');
    } finally {
      setLoading(false);
    }
  };

  const createLocation = async () => {
    const name = window.prompt('Bezeichnung des neuen Standorts:');
    if (!name || !name.trim()) return;
    try {
      const r = await api.post('/locations', { name: name.trim() });
      toast.success('Standort angelegt');
      navigate(`/locations/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Anlegen');
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" data-testid="locations-title">
              Standorte & Räume
            </h1>
            <p className="text-slate-600">{locations.length} Standorte verwalten</p>
          </div>
          <Button
            onClick={createLocation}
            data-testid="add-location-button"
            className="bg-slate-900 text-white hover:bg-slate-800"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neuer Standort
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="locations-grid">
            {locations.map((location) => (
              <button
                key={location.id}
                data-testid={`location-card-${location.id}`}
                onClick={() => navigate(`/locations/${location.id}`)}
                className="text-left bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-brand-red/40 transition-all p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-brand-red/5 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-brand-red" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-slate-900 truncate">{location.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {location.short_code && (
                          <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">
                            {location.short_code}
                          </span>
                        )}
                        <Badge className={location.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-700'}>
                          {location.is_active ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-1 mb-4 text-sm text-slate-600">
                  {location.contact_name && <p>👤 {location.contact_name}</p>}
                  {location.city && <p>📍 {location.city}</p>}
                  {location.region && <p className="text-xs text-slate-500">Areal: {location.region}</p>}
                </div>

                <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
                  <DoorOpen className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">{location.room_count} Räume</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
